// Google Analytics utility functions
// Set VITE_GA_MEASUREMENT_ID in .env file or Railway env vars

import { getEnv } from './env'

const GA_MEASUREMENT_ID = getEnv('VITE_GA_MEASUREMENT_ID') || undefined

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void
    dataLayer: unknown[]
  }
}

/**
 * Initialize Google Analytics
 * Call this once on app startup.
 *
 * The gtag snippet is already in index.html with the hardcoded
 * measurement ID (G-2DRK3GQM87). This function ensures
 * window.gtag is available for the track* helpers and, if the
 * env var overrides the ID, sends an additional config hit.
 */
export function initializeGA() {
  // gtag script is loaded via index.html; just make sure the
  // global references are wired up for our helper functions.
  window.dataLayer = window.dataLayer || []
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer.push(args)
    }
  }

  // If an env-level measurement ID is set and differs from the
  // hardcoded one, send an extra config so both streams receive data.
  if (GA_MEASUREMENT_ID && GA_MEASUREMENT_ID !== 'G-2DRK3GQM87') {
    window.gtag('config', GA_MEASUREMENT_ID)
  }
}

/**
 * Track a page view
 */
export function trackPageView(path: string, title?: string) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
    })
  }
}

/**
 * Track a custom event
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>
) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params)
  }
}

/**
 * Track pricing page interactions
 */
export function trackPricingEvent(action: string, plan?: string, billingCycle?: string) {
  trackEvent('pricing_interaction', {
    action,
    plan: plan || '',
    billing_cycle: billingCycle || '',
  })
}

/**
 * Track checkout initiation
 */
export function trackCheckoutStart(plan: string, billingCycle: string, price: number) {
  trackEvent('begin_checkout', {
    plan,
    billing_cycle: billingCycle,
    value: price,
    currency: 'USD',
  })
}

/**
 * Track download CTA click
 */
export function trackDownloadClick(location: string) {
  trackEvent('download_click', {
    location,
  })
}

/**
 * Track CTA button clicks
 */
export function trackCtaClick(ctaName: string, location: string) {
  trackEvent('cta_click', {
    cta_name: ctaName,
    location,
  })
}
